import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useFormikContext } from 'formik';

import { useIntl } from 'react-intl';

import { Grid, Typography } from '@mui/material';

import { TranslationKeys } from 'app/translations';

import format from 'app/utils/formatter';

import { useDialog } from 'app/hooks';

import { InputFieldFactory } from 'app/components/FormikField';
import type { GetInputOptionsAndLabelFunc } from 'app/components';
import { FormikModal, SubmitConfirmationDialog } from 'app/components';

import type { EmployeeSicknessFormValues } from '../../../../EmployeeSicknessLeaves.types';
import {
  EmployeeSicknessLeavesFields,
  EmployeeEmploymentWageFieldOptionsAndLabel as EmployeeSicknessLeavesFieldOptionsAndLabel,
} from '../../../../EmployeeSicknessLeaves.types';

type EmployeeEmploymentFormDialogBodyProps = {
  open: boolean;
  isEdit: boolean;
  closeDialog: () => void;
};

export const EmployeeSicknessLeaveFormDialogBody = ({
  open,
  isEdit,
  closeDialog,
}: EmployeeEmploymentFormDialogBodyProps) => {
  const intl = useIntl();
  const { initialValues, values, submitForm, resetForm } = useFormikContext<EmployeeSicknessFormValues>();

  const {
    dialogState: submitConfirmationDialogState,
    openDialog: openSubmitConfirmationDialog,
    closeDialog: closeSubmitConfirmationDialog,
  } = useDialog();

  const onClickSubmit = React.useCallback(() => {
    const hasChangedValues = JSON.stringify(initialValues) !== JSON.stringify(values);

    if (!isEdit) {
      submitForm();
    } else if (hasChangedValues) {
      openSubmitConfirmationDialog();
    } else {
      closeDialog();
    }
  }, [closeDialog, initialValues, isEdit, openSubmitConfirmationDialog, submitForm, values]);

  const submitFormAfterConfirmation = React.useCallback(() => {
    closeSubmitConfirmationDialog();
    submitForm();
  }, [closeSubmitConfirmationDialog, submitForm]);

  const getInputOptionsAndLabel = React.useCallback<GetInputOptionsAndLabelFunc>(inputName => {
    return EmployeeSicknessLeavesFieldOptionsAndLabel[inputName];
  }, []);

  const yesNoUnknownOptions = React.useMemo(() => {
    return [
      {
        value: 'Yes',
        element: intl.formatMessage({ id: TranslationKeys.yes }),
      },
      {
        value: 'No',
        element: intl.formatMessage({ id: TranslationKeys.no }),
      },
      {
        value: 'Unknown',
        element: intl.formatMessage({ id: TranslationKeys.global_selectUnknown }),
      },
    ];
  }, [intl]);

  const yesNoEmptyOptions = React.useMemo(() => {
    return [
      {
        value: 'Yes',
        element: intl.formatMessage({ id: TranslationKeys.yes }),
      },
      {
        value: 'No',
        element: intl.formatMessage({ id: TranslationKeys.no }),
      },
    ];
  }, [intl]);

  return (
    <>
      {submitConfirmationDialogState && (
        <SubmitConfirmationDialog
          open={submitConfirmationDialogState}
          onClose={closeSubmitConfirmationDialog}
          onConfirm={submitFormAfterConfirmation}
          previousValues={initialValues}
          newValues={values}
          getInputOptionsAndLabel={getInputOptionsAndLabel}
        />
      )}

      {!submitConfirmationDialogState && (
        <FormikModal
          title={
            <FormattedMessage
              id={
                isEdit
                  ? TranslationKeys.employees_actions_editSicknessLeave
                  : TranslationKeys.employees_actions_addSicknessLeave
              }
            />
          }
          show={open}
          onSubmit={onClickSubmit}
          onCancel={isEdit ? closeDialog : undefined}
          onHide={() => {
            closeDialog();
            resetForm();
          }}
          disableEnforceFocus
        >
          {isEdit && (
            <Typography variant="h6" paragraph>
              {format.date(initialValues.startDate)}
            </Typography>
          )}

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'date',
                  header: EmployeeSicknessLeavesFields.StartDate.Label,
                  name: EmployeeSicknessLeavesFields.StartDate.InputName,
                  options: EmployeeSicknessLeavesFields.StartDate.Options,
                  required: true,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'date',
                  header: EmployeeSicknessLeavesFields.EndDate.Label,
                  name: EmployeeSicknessLeavesFields.EndDate.InputName,
                  options: EmployeeSicknessLeavesFields.EndDate.Options,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'number',
                  header: EmployeeSicknessLeavesFields.PercentageAO.Label,
                  name: EmployeeSicknessLeavesFields.PercentageAO.InputName,
                  options: EmployeeSicknessLeavesFields.PercentageAO.Options,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'number',
                  header: EmployeeSicknessLeavesFields.ContractHoursWeekly.Label,
                  name: EmployeeSicknessLeavesFields.ContractHoursWeekly.InputName,
                  options: EmployeeSicknessLeavesFields.ContractHoursWeekly.Options,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'autocomplete',
                  header: EmployeeSicknessLeavesFields.NoRiskPolicy.Label,
                  name: EmployeeSicknessLeavesFields.NoRiskPolicy.InputName,
                  options: EmployeeSicknessLeavesFields.NoRiskPolicy.Options,
                  items: yesNoUnknownOptions,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'autocomplete',
                  header: EmployeeSicknessLeavesFields.SicknessPregnancyChildBirth.Label,
                  name: EmployeeSicknessLeavesFields.SicknessPregnancyChildBirth.InputName,
                  options: EmployeeSicknessLeavesFields.SicknessPregnancyChildBirth.Options,
                  items: yesNoUnknownOptions,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'autocomplete',
                  header: EmployeeSicknessLeavesFields.OrganDonated.Label,
                  name: EmployeeSicknessLeavesFields.OrganDonated.InputName,
                  options: EmployeeSicknessLeavesFields.OrganDonated.Options,
                  items: yesNoUnknownOptions,
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <InputFieldFactory
                field={{
                  type: 'autocomplete',
                  header: EmployeeSicknessLeavesFields.AccidentThirdParty.Label,
                  name: EmployeeSicknessLeavesFields.AccidentThirdParty.InputName,
                  options: EmployeeSicknessLeavesFields.AccidentThirdParty.Options,
                  items: yesNoEmptyOptions,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldFactory
                field={{
                  type: 'text',
                  header: EmployeeSicknessLeavesFields.NotesSickness.Label,
                  name: EmployeeSicknessLeavesFields.NotesSickness.InputName,
                  options: EmployeeSicknessLeavesFields.NotesSickness.Options,
                  multiline: true,
                  minRows: 5,
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <InputFieldFactory
                field={{
                  type: 'text',
                  header: EmployeeSicknessLeavesFields.NotesRecovery.Label,
                  name: EmployeeSicknessLeavesFields.NotesRecovery.InputName,
                  options: EmployeeSicknessLeavesFields.NotesRecovery.Options,
                  multiline: true,
                  minRows: 5,
                }}
              />
            </Grid>
          </Grid>
        </FormikModal>
      )}
    </>
  );
};
